<template>
  <div class="c-form__fields">
    <h4 class="c-release__title u-mb0 u-mlxs">{{ release.name }}</h4>
    <p v-if="release.description" class="c-release__description u-mlxs u-mbxs u-color-disabled">
      {{ release.description }}
    </p>
    <ul class="c-release_assets">
      <ProjectAsset
        v-for="asset of release.assets"
        :key="asset.pk"
        :asset="asset"
      />
    </ul>
    <div
      v-if="deleteRelease.err"
      class="c-message c-message--error u-align-center u-mbs"
    >
      <small>{{ deleteRelease.err }}</small>
    </div>
    <div class="u-flex-between">
      <div>
        <!-- empty div to take flex space -->
        <template v-if="release.release_notes">
          <small>
            <button class="u-color-disabled open-release-notes" @click="releaseNotesOpen = true">
              {{ t('releaseNotes') }}
            </button>
          </small>
        </template>
      </div>
      <div v-if="project.permissions.edit" class="actions">
        <button
          class="c-button c-button--small c-button--light"
          @click.prevent="releaseEditOpen = true"
        >
          <SVGIcon class="c-button__icon" name="pen" />
          <span class="c-button__label">{{ t('edit') }}</span>
        </button>
        <!-- double click to confirm delete -->
        <Popover
          trigger="manual"
          :show="confirmDelete"
          theme="danger"
          placement="top"
          @click-outside="confirmDelete = false"
        >
          <template #button>
            <button
              class="c-button c-button--small c-button--light"
              :disabled="deleteRelease.loading"
              @click.prevent="confirmDeletion"
            >
              <SVGIcon v-if="!deleteRelease.loading" class="c-button__icon" name="trash" />
              <SVGIcon v-else class="c-button__icon" name="spinner-thin" />
              <span class="c-button__label">{{ t('delete') }}</span>
            </button>
          </template>
          <template #tooltip>
            <span>{{ t('clickAgainToConfirm') }}</span>
          </template>
        </Popover>
      </div>
    </div>
    <ReleaseNotesModal
      v-if="release.release_notes"
      v-model:open="releaseNotesOpen"
      :release-name="release.name"
      :release-notes="release.release_notes"
    />
    <Modal v-model:open="releaseEditOpen" :title="t('editARelease')">
      <ProjectReleaseForm
        v-if="editReleaseForm"
        v-model="editReleaseForm"
        :error="editRelease.err"
        :loading="editRelease.loading"
        :is-edit="true"
        @submit="editRelease.exec"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  useRequest,
  Project,
  Release,
  EditRelease,
  getProjectRelease,
  editProjectRelease,
  deleteProjectRelease
} from '@/utils/api'
import { useI18n } from '@/utils/i18n'
import SVGIcon from '@/components/SVGIcon.vue'
import ProjectAsset from '@/components/ProjectAsset.vue'
import ReleaseNotesModal from '@/components/ReleaseNotesModal.vue'
import Modal from '@/components/Modal.vue'
import Popover from '@/components/Popover.vue'
import ProjectReleaseForm, { ReleaseForm } from '@/components/ProjectReleaseForm.vue'

const props = defineProps<{
  project: Project
  releaseId: number
}>()

const emit = defineEmits<{
  (e: 'deleted', release: Release): void
}>()

const { t } = useI18n({
  messages: {
    en: {
      edit: 'Edit',
      delete: 'Delete',
      releaseNotes: 'Releases notes',
      clickAgainToConfirm: 'Click again to confirm',
      editARelease: 'Edit a release'
    },
    fr: {
      edit: 'Modifier',
      delete: 'Supprimer',
      releaseNotes: 'Notes de release',
      clickAgainToConfirm: 'Re-cliquer pour confirmer',
      editARelease: 'Modifier la release'
    }
  }
})

const release = await getProjectRelease(props.releaseId)
const releaseNotesOpen = ref(false)
const confirmDelete = ref(false)

function confirmDeletion () {
  // ask user confirmation
  if (confirmDelete.value === false) {
    confirmDelete.value = true
    return
  }
  confirmDelete.value = false
  deleteRelease.exec()
}

const deleteRelease = useRequest(() => deleteProjectRelease(props.releaseId), {
  success: () => { emit('deleted', release) }
})

const releaseEditOpen = ref(false)

// init edit release form values
const editReleaseForm = ref<ReleaseForm>({
  name: release.name,
  description: release.description,
  releaseNotes: release.release_notes || '',
  assets: release.assets
})

const editRelease = useRequest(async () => {
  const f = editReleaseForm.value!
  const body = {
    name: f.name,
    description: f.description,
    release_notes: f.releaseNotes
  }
  await editProjectRelease(props.releaseId, body)
  // update displayed release
  for (const key in body) {
    const k = key as keyof EditRelease
    release[k] = body[k]!
  }
  // close modal
  releaseEditOpen.value = false
})
</script>

<style scoped>
.actions {
  display: flex;
  gap: 5px;
}

.open-release-notes {
  outline: none;
  border: none;
  background: none;
  text-decoration: underline;
}
.open-release-notes:hover {
  text-decoration: none;
}
</style>
